// src/components/tables/ResponsesTable.js
import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { useNavigate } from 'react-router-dom'; 
import API_CONFIG from '../../config/api'

const BASE_URL = API_CONFIG.BASE_URL;

ModuleRegistry.registerModules([ AllCommunityModule ]);


const ResponsesGrid = ({ surveyId }) => {
    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const gridRef = useRef();
	const navigate = useNavigate(); // Initialize useNavigate hook
    
    const { authenticatedFetch } = useAuthenticatedApi();

    // Default grid options
    const defaultColDef = {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100,
    };

    // Grid options
    const gridOptions = {
        pagination: true,
        paginationPageSize: 20,
        domLayout: 'autoHeight',
        suppressExcelExport: false,
        suppressCsvExport: false,
        columnTypes: {
            date: {
              filter: 'agDateColumnFilter',
              filterParams: {
                comparator: (filterLocalDateAtMidnight, cellValue) => {
                  // Parse the ISO string (assuming your API returns it like '2025-08-04T12:00:00Z')
                  const cellDate = new Date(cellValue);
                  if (cellDate < filterLocalDateAtMidnight) return -1;
                  if (cellDate > filterLocalDateAtMidnight) return 1;
                  return 0;
                },
              },
              // Optional: cell formatting
              valueFormatter: (params) => {
                const date = new Date(params.value);
                return date.toLocaleDateString();
              },
            },
            boolean: {
                filter: 'agSetColumnFilter',
                filterParams: {
                  values: ['Yes', 'No']
                },
                valueFormatter: (params) => {
                  return params.value ? 'Yes' : 'No';
                },
                cellRenderer: (params) => {
                  return params.value ? 'Yes' : 'No';
                }
              },
        },
    };

    useEffect(() => {
        fetchResponses();
    }, [surveyId]);

    const fetchResponses = async () => {
        setIsLoading(true);
        setError(null);

        try {
            // TODO change error handling to be exactly the same as frontend/app/src/components/ui/ManageTokensList.js fetchToken()
            const endpoint = `${BASE_URL}/active_surveys/surveys/responses/${surveyId}`;
            const result = await authenticatedFetch(endpoint, {}, "GET");
            
            if (result.status === 404) {
				navigate('/404', { state: { message: `survey with survey_id: ${surveyId} is not found`} });
				return;
			}
            if (result.status === 401) {
				setError("You are not authorized to access this survey responses, please check that you have the proper access permission and that it is an active survey");
				return;
			}
			if (!result.ok) {
				let errorMessage = `Failed to fetch responses for ${surveyId}: Server responded with status ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
				}
				console.error("ManageTokenList API Error:", errorMessage, result);
				setError(errorMessage);
				return; // Stop further execution
			}

            const data = await result.json();
            
            // Process the data from backend (using dedicated survey tables)
            if (data && data.responses && data.responses.length > 0) {
                // Use column definitions from backend
                setColumnDefs(data.column_definitions || []);
                
                // Use response data directly (already formatted by backend)
                setRowData(data.responses);
            } else if ( data.responses.length === 0){
                // No responses yet
                setColumnDefs(data.column_definitions || []);
                setRowData([]);

            }
        } catch (err) {
            console.error('Error fetching responses:', err);
            setError(err.message);
            setColumnDefs([
                { headerName: 'Error', field: 'error', width: 400 }
            ]);
            setRowData([{ error: 'Failed to load responses. Please try again.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Export functions
    const exportToCsv = () => {
        if (gridRef.current) {
            gridRef.current.api.exportDataAsCsv({
                fileName: `survey_${surveyId}_responses.csv`
            });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-500 mt-2">Loading responses...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Export Controls */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Survey Responses</h3>
                    <p className="text-sm text-gray-500">
                        {rowData.length} response{rowData.length !== 1 ? 's' : ''} found
                    </p>
                </div>
                
                <div className="flex gap-2">
                    <button
                        onClick={exportToCsv}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={rowData.length === 0}
                    >
                        Export CSV
                    </button>
                </div>
            </div>

            {/* AG Grid Table */}
            <div className="ag-theme-alpine" style={{ width: '100%', minHeight: '400px' }}>
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    gridOptions={gridOptions}
                    animateRows={true}
                    rowSelection={{ mode: 'multiRow' }}
                />
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">{error}</p>
                </div>
            )}
        </div>
    );
};

export default ResponsesGrid;